import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [signin, setSignin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true); // Nuevo estado para manejar la carga
  const navigate = useNavigate();
  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
      checkAuthentication();
    } else {
      setLoading(false);
    }
  }, [authToken]);

  const handleLogin = async (loginData) => {
    setError(""); // Clear any previous errors
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/login`,
        loginData
      );
      if (response.data.Login) {
        const token = response.data.token;
        localStorage.setItem("authToken", token);
        setAuthToken(token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setAuthenticatedUser(response.data.user);

        // Redirect after login success
        navigate("/mis-granjas");
      }
    } catch (error) {
      console.error("Error during login:", error);
      const message =
        error.response?.data?.message || "An unexpected error occurred.";
      setError(message);
    }
  };

  const handleLogout = async () => {
    const confirmLogout = window.confirm(
      "¿Estás seguro de que deseas cerrar sesión?"
    );

    if (!confirmLogout) return; // Si el usuario cancela, no se ejecuta el logout

    try {
      await axios.get(`${process.env.REACT_APP_API_URL}/logout`, {
        withCredentials: true,
      });

      // Limpiar el estado de autenticación y token
      localStorage.removeItem("authToken");
      delete axios.defaults.headers.common["Authorization"];
      setAuthToken(null);
      setAuthenticatedUser(null);

      // Redirigir al usuario a la página de inicio
      navigate("/", { replace: true });
      setSignin(!signin);
    } catch (error) {
      console.error("Error durante el cierre de sesión:", error);

      // Limpiar de todos modos en caso de error en el servidor
      localStorage.removeItem("authToken");
      delete axios.defaults.headers.common["Authorization"];
      setAuthToken(null);
      setAuthenticatedUser(null);

      // Redirigir al usuario a la página de inicio
      navigate("/", { replace: true });
      setSignin(!signin);
    }
  };

  const checkAuthentication = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/verify-auth`,
        { withCredentials: true }
      );
      if (response.data.isAuthenticated) {
        setAuthenticatedUser(response.data.user);
      } else {
        setAuthenticatedUser(null);
        localStorage.removeItem("authToken");
        setAuthToken(null);
      }
    } catch (error) {
      console.error("Error checking authentication:", error);
      setAuthenticatedUser(null);
      localStorage.removeItem("authToken");
      setAuthToken(null);
    } finally {
      setLoading(false); // Finaliza la carga después de la verificación
    }
  };

  const contextValue = {
    authenticatedUser,
    authToken,
    setAuthenticatedUser,
    handleLogin,
    handleLogout,
    signin,
    setSignin,
    error,
    setError,
    loading, // Incluir el estado de carga en el contexto
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
